import { supabase } from '../lib/supabaseClient';
import { listingProfileUrl } from '../utils/listingUrl';

/**
 * LWD Linking System, related-content (Tier 2). Derives an Edit's related
 * articles + related venues from the entity graph captured at publish time
 * (mentions_json) plus location_tags. Render-time so it's always fresh.
 * Spec: _LINKING_SYSTEM.md.
 */

const POST_CARD = 'id, slug, title, cover_image, published_at, category_label, category_slug, author_data, reading_time';
const LISTING_CARD = 'id, name, slug, country_slug, region_slug, category_slug, city, region, card_image, hero_image';

const slugify = (s) => (s ?? '').toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export function listingUrl(l) {
  return listingProfileUrl(l);
}
export function listingImage(l) {
  return l.card_image || l.hero_image || null;
}

/**
 * Reverse direction: Edits that link to a given listing (from the mentions_json
 * manifest the auto-linker emits). Powers the "Featured in The Edit" rail on a
 * venue/vendor profile. Crawlable internal links back into editorial.
 */
export async function fetchEditsMentioningListing(listingId, limit = 4) {
  if (!listingId) return [];
  const { data, error } = await supabase
    .from('magazine_posts')
    .select('id, slug, title, cover_image, hero_focal_point, category_label, published_at, reading_time')
    .eq('published', true)
    .contains('mentions_json', JSON.stringify([{ id: listingId }]))
    .order('published_at', { ascending: false })
    .order('id', { ascending: true })
    .limit(limit);
  if (error) { console.warn('[relatedContentService] fetchEditsMentioningListing', error.message); return []; }
  return data || [];
}

/**
 * @param post  the loaded post (needs id, mentions_json, location_tags, category_label)
 * @returns { relatedEdits, relatedVenues }
 */
export async function fetchRelatedForPost(post, { editLimit = 6, venueLimit = 6 } = {}) {
  if (!post?.id) return { relatedEdits: [], relatedVenues: [] };

  const mentions = Array.isArray(post.mentions_json) ? post.mentions_json : [];
  const mentionIds = [...new Set(mentions.map((m) => m?.id).filter(Boolean))];
  const mentionSet = new Set(mentionIds);
  const locSlugs = new Set((post.location_tags || []).map(slugify).filter(Boolean));
  // Also take the region slug straight from any destination the Edit links to
  // (e.g. a "Lake Como" mention → url /italy/lake-como → region 'lake-como'),
  // so same-destination fill works even when location_tags are sparse.
  for (const m of mentions) {
    if (!m?.url || !['region', 'city', 'destination', 'country'].includes(m.type)) continue;
    const parts = String(m.url).split('/').filter(Boolean);
    if (parts[1]) locSlugs.add(parts[1]);
  }

  // ── Related EDITS: score recent published posts by shared entities + shared
  //    locations + same category. Falls back to recent if the graph is sparse.
  const { data: cands } = await supabase
    .from('magazine_posts')
    .select(`${POST_CARD}, mentions_json, location_tags`)
    .eq('published', true)
    .neq('id', post.id)
    .order('published_at', { ascending: false })
    .order('id', { ascending: true })
    .limit(140);

  const scoreEdit = (c) => {
    let s = 0;
    for (const m of c.mentions_json || []) if (mentionSet.has(m?.id)) s += 3;
    for (const t of c.location_tags || []) if (locSlugs.has(slugify(t))) s += 2;
    if (c.category_label && c.category_label === post.category_label) s += 1;
    return s;
  };
  let relatedEdits = (cands || [])
    .map((c) => ({ c, s: scoreEdit(c) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || new Date(b.c.published_at) - new Date(a.c.published_at)
      || String(a.c.id).localeCompare(String(b.c.id)))   // unique tiebreak → total order
    .map((x) => x.c);
  if (relatedEdits.length < editLimit) {
    const have = new Set([post.id, ...relatedEdits.map((e) => e.id)]);
    for (const c of cands || []) {
      if (relatedEdits.length >= editLimit) break;
      if (!have.has(c.id)) { relatedEdits.push(c); have.add(c.id); }
    }
  }
  relatedEdits = relatedEdits.slice(0, editLimit);

  // ── Related VENUES: listings this Edit explicitly links to (strongest signal),
  //    then filled with other published venues in the same destination(s). Excludes
  //    this article's OWN subject venue — its self-link (for the "Featured in The
  //    Edit" rail on the venue's own profile) lives in mentions_json alongside
  //    genuine cross-linked venues, and without this exclusion a venue's own Edit
  //    page recommended itself under "Related venues".
  const ownVenueIds = new Set((post.related_listing_ids || []).filter(Boolean));
  const venueMap = new Map();
  if (mentionIds.length) {
    const { data: ml } = await supabase
      .from('listings').select(LISTING_CARD)
      .in('id', mentionIds).eq('status', 'published')
      .order('id', { ascending: true });
    for (const l of ml || []) if (l.slug && l.country_slug && l.region_slug && !ownVenueIds.has(l.id)) venueMap.set(l.id, l);
  }
  if (venueMap.size < venueLimit && locSlugs.length) {
    // One query across all location slugs, not a serial query per slug (was a bounded N+1).
    const { data: rl } = await supabase
      .from('listings').select(LISTING_CARD)
      .eq('status', 'published').in('region_slug', locSlugs)
      .order('id', { ascending: true }).limit(venueLimit * 2);
    for (const l of rl || []) {
      if (venueMap.size >= venueLimit) break;
      if (!venueMap.has(l.id) && l.slug && l.country_slug && l.region_slug && !ownVenueIds.has(l.id)) venueMap.set(l.id, l);
    }
  }
  const relatedVenues = [...venueMap.values()].slice(0, venueLimit);

  return { relatedEdits, relatedVenues };
}
